import { useScopeParams } from "@/hooks/useScopeParams";
import { useFetch } from "@/hooks/FetchHook";
import PageHeader from "@/components/UI/PageHeader";
import { MetadataDashboardTemplate } from "@/components/Templates/MetadataDashboardTemplate";
import PkConcentrationChart, { type PkRow } from "./PkConcentrationChart";

const DATASET = "pk_concentrations";

/**
 * Analysis › Pharmacokinetics.
 *
 * Purpose-built PK screen: concentration-time curves per analyte (grouped by
 * dose level, with a semi-log toggle) over the full result set, plus the
 * standard MetadataDashboardTemplate table for the per-sample rows.
 */
export default function AnalysisPharmacokinetics() {
  const { id } = useScopeParams();

  // The table paginates; the curves need the whole result set, so fetch it once
  // here (unfiltered, large page) and feed it to the chart.
  const { data, isLoading } = useFetch({
    endpointSuffix: `filters/data/?dataset_type=${DATASET}`,
    method: "POST",
    body: {
      dataset_type: DATASET,
      global_filters: [],
      plot_filters: [],
      pagination: { page: 1, page_size: 1000 },
    },
  });

  const rows: PkRow[] =
    (data as { results?: PkRow[] } | undefined)?.results ?? [];

  return (
    <div className="w-full p-4">
      <div className="mb-4 flex justify-between">
        <PageHeader
          title="Analysis: Pharmacokinetics"
          tooltipText="Concentration-time curves for each PK analyte (total antibody, intact ADC, free payload), grouped by dose level, plus the underlying per-sample concentration table. Toggle semi-log to read elimination kinetics."
        />
      </div>

      <div className="mb-6">
        <PkConcentrationChart rows={rows} isLoading={isLoading} />
      </div>

      <MetadataDashboardTemplate
        type={DATASET}
        constants={{
          wikiLink: "/wiki/guides/exploration-analysis-pharmacokinetics",
          idLink: (subjectId: string) =>
            `/${id}/subjects/overview/${subjectId}/pharmacokinetics`,
        }}
      />
    </div>
  );
}
